import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const chapters = [
  {
    n: "01",
    title: "Weight over melody",
    body: "The system comes first. Every track is engineered for the physical — pressure you feel in the chest before you hear it. No filler, no drops for the sake of it. Just tension, release and the space between.",
  },
  {
    n: "02",
    title: "Dark before light",
    body: "Trance stripped of its gloss and dragged into the basement. Hypnotic, patient, built to unfold across the night. The euphoria is earned, never handed over cheap.",
  },
  {
    n: "03",
    title: "Bristol in the blood",
    body: "Raised on the city's low-end lineage — the dub, the jungle, the sound-system culture. zi enzi is the next transmission from those same damp walls and blown speakers.",
  },
];

const BG = "https://images.unsplash.com/photo-1549046606-d8f64651f9e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Chapter = ({ c }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    className="grid grid-cols-12 gap-4 sm:gap-6 border-t border-line py-10 sm:py-14"
    data-testid={`manifesto-chapter-${c.n}`}
  >
    <div className="col-span-12 sm:col-span-4">
      <span className="stroke-text-bone font-display font-black text-7xl sm:text-8xl lg:text-9xl leading-none tracking-tighter">
        {c.n}
      </span>
    </div>
    <div className="col-span-12 sm:col-span-8">
      <h3 className="font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl text-bone tracking-tight mb-4">
        {c.title}
      </h3>
      <p className="font-mono text-sm sm:text-base leading-relaxed text-bone/60 max-w-xl">{c.body}</p>
    </div>
  </motion.div>
);

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="manifesto" ref={ref} className="relative bg-ink overflow-hidden" data-testid="manifesto-section">
      <motion.img
        style={{ y }}
        src={BG}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-1/4 w-1/2 max-w-2xl opacity-[0.14] object-cover mix-blend-luminosity"
      />
      <div className="relative px-5 sm:px-8 lg:px-12 py-20 sm:py-28 max-w-6xl">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-acid"
        >
          / Manifesto
        </motion.div>
        <h2 className="font-display font-black uppercase text-bone text-4xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter mb-14 max-w-3xl">
          Three rules for the <span className="text-acid">low end</span>
        </h2>
        {chapters.map((c) => (
          <Chapter key={c.n} c={c} />
        ))}
      </div>
    </section>
  );
};

export default Manifesto;
